import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Package, CheckCircle2, Truck, Home, X, Clock } from "lucide-react";

const STATUS_STEPS = [
    { key: "pending_payment", label: "Awaiting payment", icon: Clock },
    { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
    { key: "packed", label: "Packed", icon: Package },
    { key: "shipped", label: "Shipped", icon: Truck },
    { key: "delivered", label: "Delivered", icon: Home },
];

export default function OrderTrack() {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/orders/track/${orderNumber}`).then(res => setOrder(res.data)).catch(() => setErr("Order not found"));
    }, [orderNumber]);

    if (err) return <div className="max-w-3xl mx-auto py-32 text-center text-muted-foreground">{err}</div>;
    if (!order) return <div className="max-w-3xl mx-auto py-32 text-center">Loading…</div>;

    const stepIdx = STATUS_STEPS.findIndex(s => s.key === order.status);
    const isCancelled = order.status === "cancelled";
    const isRefunded = order.status === "refunded";

    return (
        <div className="max-w-4xl mx-auto px-6 lg:px-10" data-testid="order-track">
            <p className="font-mono-ui text-[#857C72]">Order {order.order_number}</p>
            <h1 className="font-display text-5xl mt-2">Tracking</h1>

            <div className="mt-10 dw-section p-6 sm:p-8">
                {isCancelled || isRefunded ? (
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-[#B42318]/10 text-[#B42318] flex items-center justify-center">
                            <X className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="font-medium">{isRefunded ? "Order refunded" : "Order cancelled"}</p>
                            <p className="text-sm text-muted-foreground">{order.timeline?.slice(-1)[0]?.note}</p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-5 gap-2">
                        {STATUS_STEPS.map((s, i) => {
                            const done = i <= stepIdx;
                            return (
                                <div key={s.key} className="flex flex-col items-center text-center">
                                    <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center ${done ? "bg-[#121214] text-white" : "bg-[#F4EFE6] text-[#857C72]"}`}>
                                        <s.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                                    </div>
                                    <p className={`text-[10px] sm:text-xs mt-2 font-mono-ui ${done ? "text-[#1B1B1F]" : "text-muted-foreground"}`}>{s.label}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="mt-6 dw-section p-6">
                <h3 className="font-display text-2xl mb-4">Timeline</h3>
                <div className="space-y-3">
                    {[...order.timeline].reverse().map((t, i) => (
                        <div key={i} className="flex gap-4 items-start text-sm">
                            <div className="h-2 w-2 rounded-full bg-[#121214] mt-1.5" />
                            <div className="flex-1">
                                <p className="font-medium">{t.status.replace("_", " ").toUpperCase()}</p>
                                <p className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()}</p>
                                {t.note && <p className="text-xs mt-1">{t.note}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-6 dw-section p-6">
                <h3 className="font-display text-2xl mb-4">Items</h3>
                <div className="space-y-3">
                    {order.items.map((it, i) => (
                        <div key={i} className="flex gap-4 items-center">
                            <img src={it.image} alt="" className="h-16 w-16 rounded-xl object-cover" />
                            <div className="flex-1">
                                <p className="font-medium">{it.product_title}</p>
                                <p className="text-sm text-muted-foreground">{it.color} / {it.size} · Qty {it.qty}</p>
                            </div>
                            <p className="font-medium">{formatPrice(it.subtotal)}</p>
                        </div>
                    ))}
                </div>
                <div className="h-px bg-[#E7DED2] my-4" />
                <div className="flex justify-between font-semibold"><span>Total</span><span>{formatPrice(order.total)}</span></div>
            </div>

            <div className="mt-10 text-center">
                <Link to="/shop" className="dw-btn-primary px-7 py-3 font-mono-ui">Continue shopping</Link>
            </div>
            <div className="h-24" />
        </div>
    );
}
